import React, {
  FunctionComponent,
  MouseEventHandler,
  ReactElement,
  useCallback,
  useState,
} from 'react';
import { useMedia } from 'react-use';
import { v4 } from 'uuid';

import { BUTTON_TYPES, media } from '@savgroup-front-common/constants';
import { MessageType } from '@savgroup-front-common/types';

import { CopyField } from '../../atoms/CopyField';
import messages from '../../atoms/CopyField/messages';
import { notice } from '../../atoms/Toast';
import {
  safeFormattedIntlString,
  SafeFormattedMessage,
} from '../../formatters';
import { useIsNewUiEnabled } from '../../hooks/useIsNewUiEnabled';
import useCopy from '../../protons/CopyOnClick/useCopy';

import {
  $FieldContainer,
  $FieldValue,
  $FieldValueContainer,
  $InlineFieldLabelNewUi,
  $MaxWidthDiv,
  $MobileFieldContainer,
  $SpannedButton,
} from './Field.styles';
import { FIELD_ALIGN } from './Field.types';
import FieldLabel from './FieldLabel';
import IconCopy from './IconCopy';

interface FieldProps {
  label?: string | number | MessageType;
  copy?: number | string | MessageType;
  dataTestId?: string;
  labelIcon?: ReactElement;
  labelIconAlign?: FIELD_ALIGN;
  actions?: ReactElement[];
  align?: FIELD_ALIGN;
  hasMaxWidth?: boolean;
  onChildrenClick?: MouseEventHandler<HTMLButtonElement>;
  onLabelClick?: MouseEventHandler<HTMLButtonElement>;
  noMargin?: boolean;
  isEllipsisEnabled?: boolean;
  isError?: boolean;
  isSuccess?: boolean;
  isNewUi?: boolean;
  isFluid?: boolean;
}

const Field: FunctionComponent<React.PropsWithChildren<FieldProps>> = ({
  children,
  copy,
  label,
  labelIcon,
  labelIconAlign = FIELD_ALIGN.LEFT,
  dataTestId,
  actions = [],
  align = FIELD_ALIGN.LEFT,
  onChildrenClick,
  onLabelClick,
  hasMaxWidth = false,
  noMargin = false,
  isEllipsisEnabled = true,
  isError = false,
  isSuccess = false,
  isNewUi,
  isFluid = false,
}) => {
  const isMobileView = useMedia(media.maxWidth.xs);

  const hasCopy =
    Boolean(copy) &&
    (typeof copy === 'string' ||
      typeof copy === 'number' ||
      Boolean(copy?.id && copy?.defaultMessage));

  const [toastId, setToastId] = useState(() => v4());
  const { handleClick } = useCopy();

  const handleIconClick = useCallback(
    async (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      e.preventDefault();

      if (hasCopy) {
        await handleClick(e, safeFormattedIntlString(copy));

        notice.info(messages.textCopied, {
          toastId,
          onClose: () => setToastId(v4()),
        });
      }
    },
    [copy, hasCopy, handleClick, toastId],
  );

  const defaultNewUiEnabled = useIsNewUiEnabled();
  const isNewUiEnabled = isNewUi !== undefined ? isNewUi : defaultNewUiEnabled;

  if (isMobileView) {
    return (
      <$MobileFieldContainer>
        <$InlineFieldLabelNewUi>
          {SafeFormattedMessage(label)}
        </$InlineFieldLabelNewUi>
        <span>{children}</span>
      </$MobileFieldContainer>
    );
  }

  return (
    <$FieldContainer
      $noMargin={noMargin}
      $isNewUi={isNewUiEnabled}
      $isFluid={isFluid}
    >
      <FieldLabel
        label={label}
        labelIcon={labelIcon}
        labelIconAlign={labelIconAlign}
        align={align}
        onClick={onLabelClick}
      />
      <$FieldValueContainer
        $align={align}
        $isError={isError}
        $isSuccess={isSuccess}
        $isFluid={isFluid}
      >
        <CopyField copy={hasCopy ? safeFormattedIntlString(copy) : undefined}>
          <$FieldValue
            $hasCopy={hasCopy || actions.length > 0}
            data-testid={dataTestId}
            hasMaxWidth={hasMaxWidth}
            $noMargin={noMargin}
            $isEllipsisEnabled={isEllipsisEnabled}
            $isError={isError}
            $isSuccess={isSuccess}
            $isNewUi={isNewUiEnabled}
            $isFluid={isFluid}
          >
            {align === FIELD_ALIGN.RIGHT && hasCopy ? (
              <IconCopy handleIconClick={handleIconClick} />
            ) : null}

            {align === FIELD_ALIGN.RIGHT ? actions : null}

            {onChildrenClick ? (
              <$SpannedButton
                type={BUTTON_TYPES.BUTTON}
                onClick={onChildrenClick}
                title={safeFormattedIntlString(copy)}
              >
                {children}
              </$SpannedButton>
            ) : (
              <$MaxWidthDiv
                hasMaxWidth={hasMaxWidth}
                $isFluid={isFluid}
                title={safeFormattedIntlString(copy)}
              >
                {children}
              </$MaxWidthDiv>
            )}

            {align === FIELD_ALIGN.LEFT && hasCopy ? (
              <IconCopy handleIconClick={handleIconClick} />
            ) : null}

            {align === FIELD_ALIGN.LEFT ? actions : null}
          </$FieldValue>
        </CopyField>
      </$FieldValueContainer>
    </$FieldContainer>
  );
};

export default Field;
